import * as v from "@valibot/valibot";

// ============================================================
// API Schemas
// ============================================================

import { Address, Hex, UnsignedInteger } from "../../_schemas.ts";

/**
 * Transfer between sub-accounts (perpetual).
 * @see null
 */
export const SubAccountTransferRequest = /* @__PURE__ */ (() => {
  return v.object({
    /** Action to perform. */
    action: v.object({
      /** Type of action. */
      type: v.literal("subAccountTransfer"),
      /** Sub-account address. */
      subAccountUser: Address,
      /** `true` for deposit, `false` for withdrawal. */
      isDeposit: v.boolean(),
      /** Amount to transfer (float * 1e6). */
      usd: v.pipe(UnsignedInteger, v.minValue(1)),
    }),
    /** Nonce (timestamp in ms) used to prevent replay attacks. */
    nonce: UnsignedInteger,
    /** ECDSA signature components. */
    signature: v.object({
      /** First 32-byte component. */
      r: v.pipe(Hex, v.length(66)),
      /** Second 32-byte component. */
      s: v.pipe(Hex, v.length(66)),
      /** Recovery identifier. */
      v: v.picklist([27, 28]),
    }),
    /** Expiration time of the action. */
    expiresAfter: v.optional(UnsignedInteger),
  });
})();
export type SubAccountTransferRequest = v.InferOutput<typeof SubAccountTransferRequest>;

/**
 * Successful response without specific data or error response.
 * @see null
 */
export type SubAccountTransferResponse =
  | {
    /** Successful status. */
    status: "ok";
    /** Response details. */
    response: {
      /** Type of response. */
      type: "default";
    };
  }
  | {
    /** Error status. */
    status: "err";
    /** Error message. */
    response: string;
  };

// ============================================================
// Execution Logic
// ============================================================

import { parse } from "../../../_base.ts";
import { canonicalize } from "../../../signing/mod.ts";
import {
  type ExchangeConfig,
  type ExcludeErrorResponse,
  executeL1Action,
  type ExtractRequestOptions,
} from "./_base/mod.ts";

/** Schema for action fields (excludes request-level system fields). */
const SubAccountTransferActionSchema = /* @__PURE__ */ (() => {
  return v.object(SubAccountTransferRequest.entries.action.entries);
})();

/** Action parameters for the {@linkcode subAccountTransfer} function. */
export type SubAccountTransferParameters = Omit<v.InferInput<typeof SubAccountTransferActionSchema>, "type">;

/** Request options for the {@linkcode subAccountTransfer} function. */
export type SubAccountTransferOptions = ExtractRequestOptions<v.InferInput<typeof SubAccountTransferRequest>>;

/** Successful variant of {@linkcode SubAccountTransferResponse} without errors. */
export type SubAccountTransferSuccessResponse = ExcludeErrorResponse<SubAccountTransferResponse>;

/**
 * Transfer between sub-accounts (perpetual).
 *
 * Signing: L1 Action.
 *
 * @param config General configuration for Exchange API requests.
 * @param params Parameters specific to the API request.
 * @param opts Request execution options.
 * @return Successful response without specific data.
 *
 * @throws {ValidationError} When the request parameters fail validation (before sending).
 * @throws {TransportError} When the transport layer throws an error.
 * @throws {ApiRequestError} When the API returns an unsuccessful response.
 *
 * @example
 * ```ts
 * import { HttpTransport } from "@nktkas/hyperliquid";
 * import { subAccountTransfer } from "@nktkas/hyperliquid/api/exchange";
 * import { privateKeyToAccount } from "npm:viem/accounts";
 *
 * const wallet = privateKeyToAccount("0x..."); // viem or ethers
 * const transport = new HttpTransport(); // or `WebSocketTransport`
 *
 * await subAccountTransfer({ transport, wallet }, {
 *   subAccountUser: "0x...",
 *   isDeposit: true,
 *   usd: 1 * 1e6,
 * });
 * ```
 *
 * @see null
 */
export function subAccountTransfer(
  config: ExchangeConfig,
  params: SubAccountTransferParameters,
  opts?: SubAccountTransferOptions,
): Promise<SubAccountTransferSuccessResponse> {
  const action = canonicalize(
    SubAccountTransferActionSchema,
    parse(SubAccountTransferActionSchema, { type: "subAccountTransfer", ...params }),
  );
  return executeL1Action(config, action, opts);
}
